/**
 * Contact.jsx — editorial contact form + firm address block.
 * Posts to /api/public/contact (no auth required). Uses a plain fetch to
 * avoid the axios interceptor that redirects to /login on 401.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Mail, Loader2, ArrowUpRight } from "lucide-react";

const SERVICES = [
    "Taxation Services",
    "Accounting Solutions",
    "Business Advisory",
    "NRI / International",
    "Govt & Non-Govt Projects",
    "Something else",
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "", honeypot: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Please fill name, phone and message.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API}/api/leads`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    phone: form.phone.trim(),
                    email: form.email.trim() || null,
                    service: form.service || null,
                    message: form.message.trim(),
                    // Honeypot field expected by /api/leads is `website`.
                    website: form.honeypot,
                }),
            });
            if (!res.ok) {
                const b = await res.json().catch(() => ({}));
                throw new Error(b?.detail?.message || b?.detail || "Failed to submit. Please try again.");
            }
            setSent(true);
            toast.success("We received your message. We'll be in touch shortly.");
        } catch (err) {
            toast.error(err.message || "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="font-outfit" data-testid="marketing-contact">
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-14">
                <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-5">
                    Let&apos;s talk
                </div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-[5rem] font-light tracking-tighter leading-[1.02] text-[#0A1128] max-w-4xl">
                    We&apos;d love<br />
                    <span className="italic">to hear from you.</span>
                </h1>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
                {/* FORM */}
                <div className="lg:col-span-7">
                    {sent ? (
                        <div className="p-10 border border-[#C5A059] bg-[#FDFBF7]" data-testid="contact-thanks">
                            <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-4">
                                Message received
                            </div>
                            <h2 className="font-serif text-3xl lg:text-4xl font-light tracking-tight text-[#0A1128]">
                                Thank you — we&apos;ll reach out shortly.
                            </h2>
                            <p className="mt-4 text-base font-light text-[#0A1128]/70">
                                A member of the team will contact you on the phone number you shared.
                            </p>
                            <button
                                onClick={() => { setSent(false); setForm({ name: "", phone: "", email: "", service: "", message: "", honeypot: "" }); }}
                                className="mt-6 inline-flex items-center gap-1.5 text-[13px] uppercase tracking-[0.18em] font-semibold text-[#0A1128] border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors duration-300"
                                data-testid="contact-send-another"
                            >
                                Send another message <ArrowUpRight size={14} />
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                            <EditorialField label="Your name *" testid="contact-name" value={form.name} onChange={(v) => set("name", v)} />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <EditorialField label="Phone *" testid="contact-phone" value={form.phone} onChange={(v) => set("phone", v)} />
                                <EditorialField label="Email (optional)" testid="contact-email" value={form.email} onChange={(v) => set("email", v)} type="email" />
                            </div>
                            <EditorialSelect label="Service you&apos;re interested in" testid="contact-service" value={form.service} onChange={(v) => set("service", v)} options={SERVICES} />
                            <EditorialField label="Message *" testid="contact-message" value={form.message} onChange={(v) => set("message", v)} multiline />

                            {/* Honeypot — hidden from humans, visible to bots */}
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.honeypot}
                                onChange={(e) => set("honeypot", e.target.value)}
                                style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}
                                aria-hidden="true"
                            />

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit"
                                className="inline-flex items-center gap-2 bg-[#0A1128] text-[#FAF3E0] px-8 py-4 text-[13px] uppercase tracking-[0.2em] font-semibold hover:bg-[#C5A059] hover:text-[#0A1128] transition-colors duration-300 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 size={15} className="animate-spin" /> : <ArrowUpRight size={15} />}
                                Send message
                            </button>
                        </form>
                    )}
                </div>

                {/* ADDRESS */}
                <aside className="lg:col-span-5 lg:pl-8 space-y-8" data-testid="contact-details">
                    <InfoBlock Icon={MapPin} label="Office">
                        Banipur Tiniali,<br />
                        Dibrugarh, Assam — India
                    </InfoBlock>
                    <InfoBlock Icon={Phone} label="Phone">
                        <a href="tel:+919435874898" className="hover:text-[#C5A059] transition-colors duration-200" data-testid="contact-phone-link">
                            +91 94358 74898
                        </a>
                    </InfoBlock>
                    <InfoBlock Icon={Mail} label="Email">
                        <span className="italic text-[#0A1128]/40">Coming soon.</span>
                    </InfoBlock>

                    <div className="pt-8 border-t border-[#0A1128]/10">
                        <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-3">
                            Existing clients
                        </div>
                        <p className="text-sm font-light leading-relaxed text-[#0A1128]/75">
                            For files, invoices or ticket updates, please use your
                            <a href="https://gtw.co.in/login" className="border-b border-[#0A1128] mx-1 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors duration-200">Client Login</a>
                            portal.
                        </p>
                    </div>
                </aside>
            </section>
        </div>
    );
}

/* Editorial-style form inputs — border-bottom only, no boxes */

function EditorialField({ label, testid, value, onChange, type = "text", multiline = false }) {
    const focus = "focus:border-[#0A1128] focus:outline-none";
    const base = "w-full bg-transparent border-b border-[#0A1128]/25 py-3 text-[15px] text-[#0A1128] placeholder-transparent font-light transition-colors duration-200";
    return (
        <label className="block group">
            <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#C5A059] block mb-1.5">{label}</span>
            {multiline ? (
                <textarea rows={5} value={value} onChange={(e) => onChange(e.target.value)} className={`${base} ${focus} resize-none`} data-testid={testid} />
            ) : (
                <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={`${base} ${focus}`} data-testid={testid} />
            )}
        </label>
    );
}

function EditorialSelect({ label, testid, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#C5A059] block mb-1.5">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-transparent border-b border-[#0A1128]/25 py-3 text-[15px] text-[#0A1128] font-light focus:border-[#0A1128] focus:outline-none cursor-pointer"
            >
                <option value="">— pick one —</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </label>
    );
}

function InfoBlock({ Icon, label, children }) {
    return (
        <div className="flex items-start gap-4">
            <div className="p-2 border border-[#0A1128]/15">
                <Icon size={16} className="text-[#C5A059]" strokeWidth={1.4} />
            </div>
            <div>
                <div className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#C5A059] mb-1.5">{label}</div>
                <div className="font-serif text-xl leading-snug text-[#0A1128]">{children}</div>
            </div>
        </div>
    );
}
